import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { formatDay, formatStamp, usePrefs } from '../lib/prefs.tsx';

/**
 * Who is in the company, and who has been invited.
 *
 * Only an owner can reach this — the server refuses /api/admin/ to anyone else,
 * and the tab is hidden rather than shown-and-failing. Hiding it is the softer
 * half; the server is the half that matters.
 *
 * Roles are enforced on every write now, so the control below genuinely changes
 * what somebody can do rather than labelling them.
 */

type Person = {
  id: number;
  email: string;
  name: string | null;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  disabled_at: string | null;
  last_login_at: string | null;
  role: string;
  email_verified_at: string | null;
  created_at: string;
  last_seen_at: string | null;
  sessions: number;
};

type Customer = {
  id: number; email: string; name: string | null; last_login_at: string | null;
  disabled_at: string | null; vehicles: number; first_granted: string;
};

type Invite = {
  id: number;
  email: string;
  role: string;
  created_at: string;
  expires_at: string;
  invited_by_email: string | null;
};

// Two sides of the counter. Shop roles see the whole fleet; a customer sees
// only the vehicles they have been given, which is why they are listed apart
// rather than as a fourth option in the same set.
const SHOP_ROLES = ['owner', 'technician', 'viewer'];
const ROLE_MEANING: Record<string, string> = {
  owner: 'Everything, including managing people here',
  technician: 'Scan, clear codes, record mileage and mark work done',
  viewer: 'Read only across the whole fleet. No scanning, no clearing codes',
  customer: 'Their own vehicles only, read only. Nothing else in the fleet is visible',
};

export function PeoplePanel() {
  const [data, setData] = useState<{ company: { id: number; name: string }; users: Person[]; invites: Invite[]; you: number | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('technician');
  const [busy, setBusy] = useState(false);
  const [companyDraft, setCompanyDraft] = useState('');
  // Dates follow the format chosen in the header, like every other date here.
  const { dateFormat } = usePrefs();
  const [editing, setEditing] = useState<number | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  // Which customer is being asked about, and what has been typed back. Revoking
  // can take an account with it, so it is confirmed the same way the platform
  // panel confirms a deletion.
  const [confirming, setConfirming] = useState<{ kind: 'customer' | 'invite'; id: number } | null>(null);
  const [typed, setTyped] = useState('');
  const openConfirm = (kind: 'customer' | 'invite', id: number) => {
    const same = confirming?.kind === kind && confirming.id === id;
    setConfirming(same ? null : { kind, id });
    setTyped('');
  };
  const closeConfirm = () => {
    setConfirming(null);
    setTyped('');
  };

  const load = () =>
    api
      .people()
      .then((d) => {
        setData(d);
        setCompanyDraft(d.company.name);
      })
      .then(() => api.customers().then((c) => setCustomers(c.customers)).catch(() => setCustomers([])))
      .catch((e) => setError((e as Error).message));

  useEffect(() => {
    load();
  }, []);

  const act = async (fn: () => Promise<unknown>, after?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (after) setNotice(after);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    await act(async () => {
      const r = await api.invite({ email, role });
      setEmail('');
      // The code appears only when the mail did not go. Otherwise it stays in
      // the inbox it was sent to.
      setNotice(
        r.mailSent
          ? `Invite sent to ${r.email}. It expires in seven days.`
          : `${r.notice ?? 'Mail is not configured.'} Pass this code to ${r.email} yourself: ${r.code}`,
      );
    });
  };

  if (error && !data) return <div className="card"><p className="dim small">{error}</p></div>;
  if (!data) return <div className="card"><p className="dim small">Loading people…</p></div>;

  const owners = data.users.filter((u) => u.role === 'owner').length;

  return (
    <>
      <div className="card">
        <h2>Company</h2>
        <p className="small dim">
          The name on invites and on every message sent from here. Vehicles and scans belong to it, not to any one
          person.
        </p>
        <form
          className="row wrap"
          style={{ gap: 8, alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            act(() => api.renameCompany(companyDraft), 'Company name updated.');
          }}
        >
          <label style={{ flex: '1 1 240px' }}>
            <span>Name</span>
            <input required maxLength={80} value={companyDraft} onChange={(e) => setCompanyDraft(e.target.value)} />
          </label>
          <button type="submit" disabled={busy || !companyDraft.trim() || companyDraft === data.company.name}>
            Save
          </button>
        </form>
      </div>

      <div className="card">
        <h2>People</h2>
        <p className="small dim">
          Everyone here sees the same vehicles and scan history. Roles decide what they can change.
        </p>

        {error && <div className="banner error" style={{ marginBottom: 12 }}>{error}</div>}
        {notice && <div className="banner" style={{ marginBottom: 12 }}>{notice}</div>}

        <div className="people-list">
          {data.users.map((u) => {
            const isYou = u.id === data.you;
            // The last owner cannot be demoted or removed, because a company
            // with no owner cannot invite anybody or change a role again.
            const lastOwner = u.role === 'owner' && owners === 1;
            return (
              <div key={u.id} className={`person-row${u.disabled_at ? ' is-disabled' : ''}`}>
                <div className="person-main">
                  {/* Full name first, username after it. An owner is looking
                      for a person, and the name is how they think of them; the
                      username is how that person appears elsewhere in the app,
                      so both belong here rather than one standing in for the
                      other. Falls back to the email when neither is set. */}
                  <span className="person-name">
                    {u.name || u.email}
                    {u.username && <span className="person-username">({u.username})</span>}
                    {isYou && <span className="pill tiny-pill">you</span>}
                    {u.disabled_at && <span className="pill warn tiny-pill">disabled</span>}
                  </span>
                  <span className="small dim mono">{u.email}</span>
                </div>

                <div className="person-state">
                  {!u.email_verified_at && <span className="pill warn" title="They have not entered their emailed code">unverified</span>}
                  {u.sessions === 0 && <span className="pill" title="No live session">signed out</span>}
                  {/* Both dates, always. How long somebody has had an account
                      is what makes the login date mean anything: yesterday
                      reads very differently on an account opened last week than
                      on one opened two years ago. Never signed in is stated
                      outright - it usually means an invite that was accepted
                      and then abandoned. */}
                  <span className="small dim person-dates">
                    <span title={`Account created ${formatStamp(u.created_at, dateFormat)}`}>Added {formatDay(u.created_at, dateFormat)}</span>
                    <span
                      className={u.last_login_at ? undefined : 'warn-text'}
                      title={
                        u.last_login_at
                          ? `Last signed in ${formatStamp(u.last_login_at, dateFormat)}`
                          : 'This account has never been used'
                      }
                    >
                      {u.last_login_at ? `Last login ${formatStamp(u.last_login_at, dateFormat)}` : 'Never signed in'}
                    </span>
                  </span>
                </div>

                <div className="person-actions">
                  <button
                    className="tiny ghost"
                    onClick={() => setEditing(editing === u.id ? null : u.id)}
                    title={`Change name, username or email for ${u.email}`}
                  >
                    {editing === u.id ? 'cancel' : 'edit'}
                  </button>
                  <select
                    value={u.role}
                    disabled={busy || lastOwner}
                    title={lastOwner ? 'The only owner. Make somebody else an owner first.' : ROLE_MEANING[u.role]}
                    onChange={(e) =>
                      act(
                        () => api.setRole(u.id, e.target.value),
                        `${u.email} is now a ${e.target.value}. They will need to sign in again.`,
                      )
                    }
                  >
                    {[...SHOP_ROLES, 'customer'].map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <button
                    className="tiny ghost"
                    disabled={busy || isYou || lastOwner}
                    title={isYou ? 'You cannot remove your own account' : lastOwner ? 'The only owner' : `Remove ${u.email}`}
                    onClick={() => act(() => api.removePerson(u.id), `${u.email} has been removed.`)}
                  >
                    remove
                  </button>
                  <button
                    className="tiny ghost"
                    disabled={busy || isYou || (lastOwner && !u.disabled_at)}
                    title={
                      u.disabled_at
                        ? 'Let them sign in again'
                        : 'Keep the account and its history, but stop them signing in'
                    }
                    onClick={() =>
                      act(
                        () => api.setPersonDisabled(u.id, !u.disabled_at),
                        u.disabled_at ? `${u.email} can sign in again.` : `${u.email} has been disabled.`,
                      )
                    }
                  >
                    {u.disabled_at ? 'enable' : 'disable'}
                  </button>
                </div>

                {editing === u.id && (
                  <form
                    className="person-edit"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const f = new FormData(e.currentTarget as HTMLFormElement);
                      act(
                        () =>
                          api.updatePerson({
                            userId: u.id,
                            firstName: String(f.get('firstName') ?? ''),
                            lastName: String(f.get('lastName') ?? ''),
                            username: String(f.get('username') ?? ''),
                            email: String(f.get('email') ?? ''),
                          }),
                        `${u.email} updated.`,
                      ).then(() => setEditing(null));
                    }}
                  >
                    <label><span>First</span><input name="firstName" defaultValue={u.first_name ?? ''} maxLength={80} /></label>
                    <label><span>Last</span><input name="lastName" defaultValue={u.last_name ?? ''} maxLength={80} /></label>
                    <label><span>Username</span><input name="username" defaultValue={u.username ?? ''} maxLength={80} /></label>
                    <label><span>Email</span><input name="email" type="email" defaultValue={u.email} required /></label>
                    <button className="tiny" type="submit" disabled={busy}>save</button>
                  </form>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* People above lists staff. Customers are not staff - they sit in the
          customers company and never appear there - so "who outside this
          company can see our vehicles" had no answer short of opening every
          record one at a time. */}
      <div className="card">
        <div className="row wrap" style={{ gap: 8, marginBottom: 4 }}>
          <h2 style={{ margin: 0, flex: 1 }}>Customers</h2>
          {customers.length > 0 && <span className="pill">{customers.length}</span>}
        </div>
        <p className="small dim">
          People outside this company who have been given one of your vehicles. They see only what you shared, and
          may hold cars from other providers as well.
        </p>

        {customers.length === 0 ? (
          <p className="small dim" style={{ marginTop: 10 }}>
            Nobody yet. Share a vehicle from its Owner access panel.
          </p>
        ) : (
          <div className="people-list">
            {customers.map((c) => (
              <div key={c.id} className={`person-row${c.disabled_at ? ' is-disabled' : ''}`}>
                <div className="person-main">
                  <span className="person-name">
                    {c.name || c.email}
                    {c.disabled_at && <span className="pill warn tiny-pill">disabled</span>}
                  </span>
                  <span className="small dim mono">{c.email}</span>
                  <span className="small dim">
                    {c.vehicles} of your vehicle{c.vehicles === 1 ? '' : 's'} · since {formatDay(c.first_granted, dateFormat)}
                    {c.last_login_at ? ` · last signed in ${formatDay(c.last_login_at, dateFormat)}` : ' · never signed in'}
                  </span>
                </div>
                <div className="person-actions">
                  {/* Revoking, not deleting. The account may hold cars from
                      other providers and those are not ours to take away.
                      Deleting an account is a platform action. */}
                  <button
                    className="tiny ghost"
                    disabled={busy}
                    title={`Stop sharing all ${c.vehicles} of your vehicles with ${c.email}`}
                    onClick={() => {
                      openConfirm('customer', c.id);
                    }}
                  >
                    remove
                  </button>
                </div>
                {confirming?.kind === 'customer' && confirming.id === c.id && (
                  <form
                    className="person-confirm"
                    onSubmit={(e) => {
                      e.preventDefault();
                      act(async () => {
                        const r = await api.revokeCustomer(c.id);
                        setNotice(
                          r.deleted
                            ? `${r.email} no longer has access, and their account has been removed.`
                            : `${r.email} can no longer see your vehicles. Their account remains, because ${r.heldElsewhere} vehicle${r.heldElsewhere === 1 ? ' is' : 's are'} still shared with them by somebody else.`,
                        );
                      }).then(closeConfirm);
                    }}
                  >
                    <p className="small">
                      This stops sharing your {c.vehicles} vehicle{c.vehicles === 1 ? '' : 's'} with{' '}
                      {c.name || c.email}. If nobody else is sharing a car with them, their account
                      is removed as well.
                    </p>
                    <p className="confirm-ask">
                      Type <strong>{c.email}</strong> below to confirm.
                    </p>
                    <div className="confirm-row">
                      <input
                        className="confirm-input"
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        placeholder={c.email}
                        autoFocus
                        autoComplete="off"
                        spellCheck={false}
                        aria-label={`Type ${c.email} to confirm`}
                      />
                      <button
                        className="tiny danger"
                        type="submit"
                        disabled={busy || typed.trim().toLowerCase() !== c.email.toLowerCase()}
                      >
                        stop sharing
                      </button>
                      <button className="tiny ghost" type="button" disabled={busy} onClick={closeConfirm}>
                        cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h2>Invite somebody</h2>
        <p className="small dim">
          They get a code by email and choose their own password. The code only works from the address it was sent to.
        </p>
        <form className="row wrap" style={{ gap: 8, alignItems: 'flex-end' }} onSubmit={invite}>
          <label style={{ flex: '1 1 220px' }}>
            <span>Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="them@example.com" />
          </label>
          <label>
            <span>Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <optgroup label="Shop">
                {SHOP_ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </optgroup>
              <optgroup label="Consumer">
                <option value="customer">customer</option>
              </optgroup>
            </select>
          </label>
          <button type="submit" disabled={busy || !email}>Send invite</button>
        </form>
        <p className="small dim" style={{ marginTop: 8 }}>{ROLE_MEANING[role]}</p>

        {data.invites.length > 0 && (
          <div className="people-list" style={{ marginTop: 14 }}>
            {data.invites.map((i) => (
              <div key={i.id} className="person-row">
                <div className="person-main">
                  <span className="person-name mono">{i.email}</span>
                  <span className="small dim">
                    Invited as {i.role}
                    {i.invited_by_email ? ` by ${i.invited_by_email}` : ''}, expires {formatDay(i.expires_at, dateFormat)}
                  </span>
                </div>
                <div className="person-actions">
                  <button
                    className="tiny ghost"
                    disabled={busy}
                    title={`Cancel the invite to ${i.email}`}
                    onClick={() => openConfirm('invite', i.id)}
                  >
                    revoke
                  </button>
                </div>
                {confirming?.kind === 'invite' && confirming.id === i.id && (
                  <form
                    className="person-confirm is-edit"
                    onSubmit={(e) => {
                      e.preventDefault();
                      act(() => api.revokeInvite(i.id), `Invite to ${i.email} revoked.`).then(closeConfirm);
                    }}
                  >
                    {/* No typing here. Revoking an invite destroys nothing that
                        cannot be sent again in one click. */}
                    <p className="confirm-ask">
                      Cancel the invite to <strong>{i.email}</strong>? Their code stops working.
                    </p>
                    <div className="confirm-row">
                      <button className="tiny" type="submit" disabled={busy}>
                        revoke invite
                      </button>
                      <button className="tiny ghost" type="button" disabled={busy} onClick={closeConfirm}>
                        keep it
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
